import React, { useEffect, useState, useCallback } from "react";
import BRAND from "../../brand";
import { facilityAdmin } from "../../api";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

// Facility-admin configuration screen for the canned-message / FAQ
// library: pre-written, pre-translated messages staff can send to a
// patient's ticket by picking the English version. The patient sees
// whichever language column matches their own ticket's language on the
// tracking page — see docs/PATIENT_LOCALIZATION.md.
export default function FAQPage() {
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await facilityAdmin.listCannedMessages();
      setMessages(r.data?.results || r.data);
    } catch { toast.error("Failed to load messages."); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      if (form.id) await facilityAdmin.updateCannedMessage(form.id, form);
      else         await facilityAdmin.createCannedMessage(form);
      toast.success(`Message ${form.id ? "updated" : "created"}.`);
      setModal(null); load();
    } catch {
      toast.error("Failed to save message.");
    } finally { setSaving(false); }
  };

  const toggleActive = async (msg) => {
    try {
      await facilityAdmin.updateCannedMessage(msg.id, { is_active: !msg.is_active });
      load();
    } catch { toast.error("Failed to update message."); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      await facilityAdmin.deleteCannedMessage(deleteConfirm.id);
      toast.success("Deleted.");
      setDeleteConfirm(null); load();
    } catch { toast.error("Delete failed."); }
    finally  { setSaving(false); }
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="FAQ / Canned Messages"
        subtitle="Pre-write messages here once, in whichever languages you have. Staff pick a message by its English text from a patient's ticket — the patient sees it in the language they chose at the kiosk."
        action={<Btn onClick={() => setModal("create")}>+ New Message</Btn>}
      />

      {loading ? <LoadingSpinner /> : messages.length === 0 ? (
        <EmptyState
          icon="💬" title="No messages yet"
          subtitle='Add one, e.g. "Please have your ID ready" — translate it into as many languages as you can, and staff will be able to send it to any patient regardless of which language they picked.'
          action={<Btn onClick={() => setModal("create")}>+ Create First Message</Btn>}
        />
      ) : (
        <div style={styles.list}>
          {messages.map(msg => (
            <div key={msg.id} style={styles.row}>
              <div style={styles.rowMain}>
                <div style={styles.rowText}>{msg.text_en}</div>
                <div style={styles.rowLangs}>
                  {(BRAND.englishOnly ? [] : ["fr", "tw", "ar", "zh"]).map(code => (
                    msg[`text_${code}`] ? (
                      <span key={code} style={styles.langChip}>{code.toUpperCase()}</span>
                    ) : null
                  ))}
                  {!msg.is_active && <span style={styles.inactiveChip}>Inactive</span>}
                </div>
              </div>
              <div style={styles.rowActions}>
                <Btn size="sm" variant="ghost" onClick={() => toggleActive(msg)}>
                  {msg.is_active ? "Deactivate" : "Activate"}
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => setModal(msg)}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm(msg)}>Delete</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <MessageFormModal
        open={!!modal}
        onClose={() => setModal(null)}
        initial={modal !== "create" ? modal : null}
        onSave={save}
        saving={saving}
      />
      <ConfirmModal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        loading={saving}
        title="Delete message?"
        message={`"${deleteConfirm?.text_en}" will be permanently deleted. Messages already sent to patients are unaffected.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

function MessageFormModal({ open, onClose, initial, onSave, saving }) {
  const blank = { text_en: "", text_fr: "", text_tw: "", text_ar: "", text_zh: "" };
  const [form, setForm] = useState(blank);
  useEffect(() => {
    if (open) setForm(initial ? {
      text_en: initial.text_en || "", text_fr: initial.text_fr || "",
      text_tw: initial.text_tw || "", text_ar: initial.text_ar || "",
      text_zh: initial.text_zh || "",
    } : blank);
  }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit Message" : "New Message"} width="520px">
      <div style={F.form}>
        <FormField label="English" required hint="Staff always see and pick messages by this text">
          <input style={inputStyle} value={form.text_en} onChange={e => set("text_en", e.target.value)} placeholder="e.g. Please have your ID ready" />
        </FormField>
        {!BRAND.englishOnly && <FormField label="French" hint="Optional — leave blank to fall back to English">
          <input style={inputStyle} value={form.text_fr} onChange={e => set("text_fr", e.target.value)} placeholder="Version française" />
        </FormField>}
        {!BRAND.englishOnly && <FormField label="Twi" hint="Optional — leave blank to fall back to English">
          <input style={inputStyle} value={form.text_tw} onChange={e => set("text_tw", e.target.value)} placeholder="Twi version" />
        </FormField>}
        {!BRAND.englishOnly && <FormField label="Arabic" hint="Optional — leave blank to fall back to English">
          <input style={inputStyle} value={form.text_ar} onChange={e => set("text_ar", e.target.value)} placeholder="النسخة العربية" dir="rtl" />
        </FormField>}
        {!BRAND.englishOnly && <FormField label="Chinese" hint="Optional — leave blank to fall back to English">
          <input style={inputStyle} value={form.text_zh} onChange={e => set("text_zh", e.target.value)} placeholder="中文版本" />
        </FormField>}
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onSave(initial ? { ...form, id: initial.id } : form)} disabled={saving || !form.text_en.trim()}>
            {saving ? "Saving…" : initial ? "Save Changes" : "Create Message"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" },
  list: { display: "flex", flexDirection: "column", gap: "10px" },
  row: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: "16px", padding: "14px 18px", borderRadius: "12px",
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)",
  },
  rowMain: { flex: 1, minWidth: 0 },
  rowText: { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" },
  rowLangs: { display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" },
  langChip: {
    fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "4px",
    background: "rgba(64,141,201,0.1)", color: "#408DC9", border: "1px solid rgba(64,141,201,0.2)",
  },
  inactiveChip: {
    fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "4px",
    background: "rgba(100,116,139,0.1)", color: "var(--text-muted)", border: "1px solid rgba(100,116,139,0.2)",
  },
  rowActions: { display: "flex", gap: "6px", flexShrink: 0 },
};

const F = {
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  footer: { display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)", marginTop: "4px" },
};
